import type { FC } from "react";

import { FieldError } from "@/shared/components/ui/FieldError";
import { Input } from "@/shared/components/ui/input";
import { CurrencyInput } from "@/shared/components/ui/CurrencyInput";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { UploadZone } from "@/shared/components/ui/upload-zone";
import { ACCOUNT_TYPES } from "@/shared/constants/estateCategories";
import { currencyInput } from "@/shared/utils/currencyInput";

const POLICY_TYPES = ACCOUNT_TYPES["Life insurance"];

export interface LifeInsuranceFormValues {
  policyType: string;
  companyName: string;
  beneficiaries: string;
  policyNumber: string;
  coverageAmount: string;
  documentFile: File | null;
}

export const EMPTY_LIFE_INSURANCE_FORM: LifeInsuranceFormValues = {
  policyType: "",
  companyName: "",
  beneficiaries: "",
  policyNumber: "",
  coverageAmount: "",
  documentFile: null,
};

interface LifeInsuranceFormProps {
  form: LifeInsuranceFormValues;
  onChange: (form: LifeInsuranceFormValues) => void;
  fieldErrors?: LifeInsuranceFieldErrors;
  showDocumentUpload?: boolean;
  existingDocumentFileName?: string | null;
  onDocumentRemoved?: () => void;
}

export const LifeInsuranceForm: FC<LifeInsuranceFormProps> = ({
  form,
  onChange,
  fieldErrors,
  showDocumentUpload = true,
  existingDocumentFileName,
  onDocumentRemoved,
}) => {
  const coverageAmountInput = currencyInput(form.coverageAmount, (val) =>
    onChange({ ...form, coverageAmount: val }),
  );

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex flex-col gap-1.5">
        <Label className="text-sm font-medium">Policy type</Label>
        <Select
          value={form.policyType}
          onValueChange={(value) => onChange({ ...form, policyType: value })}
        >
          <SelectTrigger className="text-sm">
            <SelectValue placeholder="Select property category" />
          </SelectTrigger>
          <SelectContent>
            {POLICY_TYPES.map((type) => (
              <SelectItem key={type} value={type} className="text-sm">
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="text-sm font-medium" required>
          Insurance company
        </Label>
        <Input
          placeholder="Enter company name"
          value={form.companyName}
          onChange={(e) => onChange({ ...form, companyName: e.target.value })}
          className={fieldErrors?.companyName ? "border-destructive" : ""}
        />
        <FieldError message={fieldErrors?.companyName} />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="text-sm font-medium">
          Beneficiaries <span className="text-tertiary">(if known)</span>
        </Label>
        <Input
          placeholder="Enter beneficiary names"
          value={form.beneficiaries}
          onChange={(e) => onChange({ ...form, beneficiaries: e.target.value })}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label className="text-sm font-medium">
            Policy number <span className="text-tertiary">(optional)</span>
          </Label>
          <Input
            placeholder="e.g., PL-123456789"
            value={form.policyNumber}
            onChange={(e) => onChange({ ...form, policyNumber: e.target.value })}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-sm font-medium">
            Approximate coverage amount <span className="text-tertiary">(optional)</span>
          </Label>
          <CurrencyInput
            value={coverageAmountInput.displayValue}
            onChange={coverageAmountInput.handleChange}
            placeholder="0.00"
          />
        </div>
      </div>

      {showDocumentUpload && (
        <UploadZone
          label="Do you have the policy document?"
          labelSuffix="(optional)"
          file={form.documentFile}
          onFileSelect={(file) => onChange({ ...form, documentFile: file })}
          onFileRemove={() => {
            onChange({ ...form, documentFile: null });
            onDocumentRemoved?.();
          }}
          existingFileName={existingDocumentFileName}
        />
      )}
    </div>
  );
};

export interface LifeInsuranceFieldErrors {
  companyName?: string;
}

export function validateLifeInsuranceFields(
  form: LifeInsuranceFormValues,
): LifeInsuranceFieldErrors {
  if (!form.companyName.trim()) return { companyName: "Please enter the insurance company name" };
  return {};
}

export function parseItemToForm(
  itemName: string,
  itemType: string,
  balance: number,
  notes?: string,
  metadata?: Record<string, unknown>,
): LifeInsuranceFormValues {
  const beneficiaries = (metadata?.beneficiary as string) || (notes || "").split("|||")[0] || "";
  const policyNumber = (metadata?.policy_number as string) || (notes || "").split("|||")[1] || "";
  return {
    policyType: itemType || "",
    companyName: itemName,
    beneficiaries,
    policyNumber,
    coverageAmount: balance ? String(balance) : "",
    documentFile: null,
  };
}

export function buildMetadata(form: LifeInsuranceFormValues): Record<string, unknown> {
  return {
    beneficiary: form.beneficiaries.trim() || undefined,
    policy_number: form.policyNumber.trim() || undefined,
  };
}
